import type { Theme as VollTheme } from '../types';
import type { Dienstort } from '../utils/dienstort';
export type Theme = Pick<VollTheme, 'bg' | 'bgCard' | 'bgInput' | 'text' | 'textMuted' | 'border' | 'accent' | 'accentHover' | 'success' | 'warning' | 'danger'>;

export const lightTheme: Theme = {
  bg: '#f8fafc', bgCard: '#ffffff', bgInput: '#ffffff',
  text: '#1e293b', textMuted: '#64748b', border: '#e2e8f0',
  accent: '#3b82f6', accentHover: '#2563eb',
  success: '#22c55e', warning: '#f59e0b', danger: '#ef4444',
};

export const darkTheme: Theme = {
  bg: '#0f172a', bgCard: '#1e293b', bgInput: '#334155',
  text: '#f1f5f9', textMuted: '#94a3b8', border: '#334155',
  accent: '#3b82f6', accentHover: '#60a5fa',
  success: '#22c55e', warning: '#f59e0b', danger: '#ef4444',
};

export interface HelferUser {
  id: number;
  username: string;
  name: string;
  role: string;
  email: string | null;
  registrationStatus: string;
  /** E3 (`/auth/me`): Zugang zum Portal; fuer Verwaltungskonten ohne `verknuepft` kein Zutritt */
  portal?: { zugang: 'verknuepft' | 'mitmachen' | 'wartet' | 'keiner' | 'unbekannt'; gliederungName?: string; start?: 'portal' };
}

export interface HelferEvent {
  eventId: number;
  eventName: string;
  eventDate: string;
  /** Letzter Tag — bei mehrtägigen Ereignissen verschieden vom ersten. */
  eventEndDate?: string;
  startTime: string | null;
  endTime: string | null;
  description: string | null;
  dienststelle: string | null;
  /** #596/#597: Dienstort (eigener oder der Dienststelle), null = keiner */
  dienstort?: Dienstort | null;
  /** #598: alle Tage mit Ort je Tag — Lücken dazwischen = einzelne Termine */
  tage?: { date: string; dienstort: Dienstort | null }[];
  isAbsent: boolean;
  isAssigned: boolean;
  withdrawalStatus: 'none' | 'pending' | 'confirmed';
  allowWithdrawal: boolean;
  eventHelperId: number;
  /** #368: Anmeldung liegt vor, ist aber noch nicht in den Plan uebernommen. */
  registrationPending?: boolean;
  registeredAt?: string;
  publicToken: string | null;
  shiftNames: string[];
  /** #325: Schichten, auf die der Helfer eingeteilt ist (fuer die Spalte "Eingeteilt"/"n.n"). */
  assignedShiftIds?: number[];
  isMultiDay?: boolean;
  isArchived?: boolean;
  days?: {
    id: number;
    name: string;
    date: string;
    /** #598: Ort des Tages (eigener, sonst der des Ereignisses) */
    dienstort?: Dienstort | null;
    shifts: { id: number; name: string; startTime: string | null; endTime: string | null }[];
  }[];
  /** #488: Treffpunkt, Mitbringliste, Ansprechpartner — nur bei kommender Einteilung */
  einsatz?: HelferEinsatz;
  /** #621: die EIGENE Shuttle-Wahl in Worten; `je` (V6.72.0) die eigene Abfahrt je eingeteilter Schicht — keine anderen Personen */
  shuttle?: { art: 'punkt' | 'selbst'; punkt: string | null; text: string; je?: ShuttleEigeneAbfahrt[] };
}

export interface ShuttleEigeneAbfahrt {
  shiftId: number | null;
  /** „Fr Spät“; null bei Ereignissen ohne Schichten */
  schicht: string | null;
  zustand: 'abfahrt' | 'vorOrt' | 'selbst' | 'folgt' | 'ohneAngabe';
  zeit?: string;
  ort?: string;
  adresse?: string | null;
}

export interface HelferEinsatz {
  /** Freitext der Planung an die Helfenden */
  hinweis?: string | null;
  treffpunkte: { shiftId: number | null; schicht: string; treffpunkt: string | null; treffzeit: string | null; w3w: string | null; /** #490 */ nav?: string | null }[];
  mitbringen: string[];
  ansprechpartner: { name: string; telefon: string; email?: string | null; organisation: string | null }[];
}

export interface HelferProfile {
  name: string;
  email: string | null;
  organization: string | null;
  registrationStatus: string;
  createdAt: string;
  helperData: {
    firstName: string;
    lastName: string;
    /** #518: Zeitpunkt des Profilbilds (null = keins) */
    profilbildAm?: string | null;
    phone: string | null;
    birthDate: string | null;
    qualification: string | null;
    leadership: string | null;
    driverLicense: string | null;
    digitalRadioTraining: boolean;
    /** #435: null = nie beantwortet */
    einweisungRtw?: boolean | null;
    einweisungKtw?: boolean | null;
    organization: string | null;
    street: string | null;
    zipCode: string | null;
    city: string | null;
    languages: string | null;
    allergies: string | null;
    vehicles: string | null;
    kreisverband: string | null;
    ortsverein: string | null;
    bankAccountHolder: string | null;
    iban: string | null;
    bic: string | null;
    notifNewEvents: 'instant' | 'daily' | 'weekly' | 'never';
    notifAnnouncements: 'instant' | 'daily' | 'weekly' | 'never';
    notifAssignment: 'instant' | 'daily' | 'weekly' | 'never';
    notifWithdrawalConfirm: 'instant' | 'never';
    notifNachrichten: 'instant' | 'never';
    /** #601: Sammelmail „Offene Dienste“ */
    notifOffeneDienste?: 'daily' | 'weekly' | 'never';
  } | null;
  dienststellen: { id: number; name: string }[];
}

export interface AvailableEvent {
  eventId: number;
  eventName: string;
  eventDate: string;
  /** Letzter Tag — bei mehrtägigen Ereignissen verschieden vom ersten. */
  eventEndDate?: string;
  description: string | null;
  dienststelle: string | null;
  /** #596/#597 */
  dienstort?: Dienstort | null;
  /** #598: alle Tage mit Ort je Tag */
  tage?: { date: string; dienstort: Dienstort | null }[];
  surveyToken: string;
  startTime: string | null;
  endTime: string | null;
  hasApplied: boolean;
  /** #400: Durch welche Tür man sich melden kann — und ob überhaupt noch Platz ist. */
  portalAnmeldung?: boolean;
  linkAnmeldung?: boolean;
  voll?: boolean;
  wartelisteMoeglich?: boolean;
  /** #400: Pflichtangaben, die dem Konto für dieses Ereignis fehlen. */
  fehlend?: { key: string; label: string }[];
}

export interface Dienststelle {
  id: number;
  name: string;
  /** #413: Was diese Dienststelle ist — gezeigt bei der Registrierung. */
  beschreibung?: string | null;
}
